import '../styles/globals.css'
import '../styles/hire.css'
import { setMixpanelUser } from '../config/mixpanel'
import '../styles/seeker.css'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { MediaContextProvider } from '../commonComponents/Media'
import { AxiosInterceptor } from '../config/axios'
import ErrorBoundary from '../commonComponents/ErrorBoundary'
import { CookieWrapper } from '../commonComponents/CookieWrapper'
import { useEffect, useState } from 'react'
import { LOADER_EXCLUDE } from '../constants/all'
import backgroundIMG from '../../public/assets/background10.webp'
import { SharedContextProvider } from '../context/SharedContext'
import { AdvertiserContextProvider } from '../context/AdvertiserContext'
import { SeekerContextProvider } from '../context/SeekerContext'

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter()
  const hirePathRegex = /^(?:\/hire|\/recruit)(?:[\/?#].*)?$/
  const isHire = hirePathRegex.test(router?.asPath)
  const isSeekerProfile = router?.asPath?.includes('seeker_profile')
  const [skeletonloader, setSkeletonloader] = useState({ isVisible: false, pathName: '' })

  useEffect(() => {
    const handleStart = (url: any) => {
      if (!LOADER_EXCLUDE?.includes(url))
        setSkeletonloader?.({ isVisible: true, pathName: 'defaultLoader' })
    }
    const handleComplete = (url: any) => {
      setSkeletonloader?.({ isVisible: false, pathName: 'defaultLoader' })
    }

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleComplete)
    router.events.on('routeChangeError', handleComplete)

    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleComplete)
      router.events.off('routeChangeError', handleComplete)
    }
  }, [router])

  useEffect(() => {
    //@ts-ignore
    import('antd/dist/reset.css')
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasShownTooltip = sessionStorage.getItem('hasShownTooltip')
      if (hasShownTooltip === null) {
        sessionStorage.setItem('hasShownTooltip', 'false')
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      //@ts-ignore
      const loginDetails = JSON.parse(localStorage.getItem('loginDetails'))

      if (loginDetails && loginDetails?.id) {
        setMixpanelUser(
          loginDetails?.id,
          loginDetails?.mobileNumber ? { mobileNumber: loginDetails?.mobileNumber } : {}
        )
      }
    }
  }, [])

  return (
    <>
      <Head>
        <title>
          {!isHire
            ? 'Job Openings & Vacancies - Find the Perfect Job'
            : 'Hire staff with a job post | Lokal Hiring Portal'}
        </title>
        {/* <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_APP_URL}assets/${
            isHire ? 'recruiter_open_graph.jpg' : 'seeker_open_graph.jpg'
          }`}
        /> */}
        <link rel="preload" href={backgroundIMG.src} as="image" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@100;300;400;600;700&display=swap"
        />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_BASE_URL}${router.asPath}`} />
        <meta property="og:site_name" content="Jobs Website | GetLokalApp" />
        <meta
          property="twitter:url"
          content={`${process.env.NEXT_PUBLIC_BASE_URL}${router.asPath}`}
        />
        <meta property="twitter:site" content="@getlokalapp" />
        <meta property="twitter:creator" content="@getlokalapp" />
      </Head>
      <MediaContextProvider>
        <ErrorBoundary>
          <CookieWrapper>
            <>
              {!isSeekerProfile && (
                <>
                  <Script
                    dangerouslySetInnerHTML={{
                      __html: `(function(i,s,o,g,r,a,m,n){i.moengage_object=r;t={};q=function(f){return function(){(i.moengage_q=i.moengage_q||[]).push({f:f,a:arguments})}};f=['track_event','add_user_attribute','add_first_name','add_last_name','add_email','add_mobile','add_user_name','add_gender','add_birthday','destroy_session','add_unique_user_id','moe_events','call_web_push','track','location_type_attribute'],h={onsite:["getData","registerCallback"]};for(k
            in f){t[f[k]]=q(f[k])}for(k in h)for(l in h[k]){null==t[k]&&(t[k]={}),t[k][h[k][l]]=q(k+"."+h[k][l])}a=s.createElement(o);m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m);i.moe=i.moe||function(){n=arguments[0];return t};a.onload=function(){if(n){i[r]=moe(n)}}})(window,document,'script','https://cdn.moengage.com/webpush/moe_webSdk.min.latest.js','Moengage')`
                    }}
                  ></Script>
                  <Script
                    dangerouslySetInnerHTML={{
                      __html: `
          Moengage = moe({
            app_id: '${process.env.NEXT_PUBLIC_MOENGAGE_APP_ID}',
            debug_logs: ${process.env.NEXT_PUBLIC_DEBUG_LOGS},
            enableSPA: true,
            cluster: "DC_3"
          });`
                    }}
                  ></Script>
                </>
              )}
              <SharedContextProvider
                skeletonloader={skeletonloader}
                setSkeletonloader={setSkeletonloader}
              >
                {isHire ? (
                  <AdvertiserContextProvider>
                    <AxiosInterceptor>
                      <Component {...pageProps} />
                    </AxiosInterceptor>
                  </AdvertiserContextProvider>
                ) : (
                  <SeekerContextProvider>
                    <AxiosInterceptor>
                      <Component {...pageProps} />
                    </AxiosInterceptor>
                  </SeekerContextProvider>
                )}
              </SharedContextProvider>
            </>
          </CookieWrapper>
        </ErrorBoundary>
      </MediaContextProvider>
    </>
  )
}

export const pageScripts = () => {
  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GTAG_ID}`}
      />
      <Script id="google-analytics">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${process.env.NEXT_PUBLIC_GTAG_ID}');
        `}
      </Script>

      <Script>
        {`
          function gtag_report_conversion(url) { var callback = function () { if (typeof(url) != 'undefined') { window.location = url; } }; gtag('event', 'conversion', { 'send_to': 'AW-786137828/eMQ-CO7P5osYEOSF7vYC', 'event_callback': callback }); return false; }
        `}
      </Script>

      <Script
        id="fb-pixel"
        dangerouslySetInnerHTML={{
          __html: `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${process.env.NEXT_PUBLIC_FB_ID}');
          fbq('track', 'PageView');
          `
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          //@ts-ignore
          style="display:none"
          src={`https://www.facebook.com/tr?id=${process.env.NEXT_PUBLIC_FB_ID}&ev=PageView&noscript=1`}
          alt="fb_img"
        />
      </noscript>

      <Script>
        {`(function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:3406110,hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}
      </Script>
    </>
  )
}

export const seekersPageScripts = () => {
  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_SEEKERS_GTAG_ID}`}
        defer
      />
      <Script id="google-analytics">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${process.env.NEXT_PUBLIC_SEEKERS_GTAG_ID}');
        `}
      </Script>

      <Script
        id="fb-pixel"
        dangerouslySetInnerHTML={{
          __html: `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${process.env.NEXT_PUBLIC_FB_ID_SEEKER}');
          fbq('track', 'PageView');
          `
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          //@ts-ignore
          style="display:none"
          src={`https://www.facebook.com/tr?id=${process.env.NEXT_PUBLIC_FB_ID_SEEKER}&ev=PageView&noscript=1`}
          alt="fb_img"
        />
      </noscript>
    </>
  )
}
